import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInBrandLogo } from '@l2u/web-shared';
import { ErrorMessage } from '@l2u/web-shared';
import { CenteredSpinner } from '@l2u/web-shared';
import { PoweredByFooter } from '@l2u/web-shared';
import { setAdminAccessToken } from '@l2u/web-shared';
import { createEphemeralAdminSupabase, getAdminSupabase } from '@l2u/web-shared';
import {
  SignInButton,
  SignInField,
  SignInHeading,
  SignInHelp,
  SignInLayout,
  SignInPasswordInput,
  SignInRule,
} from '@l2u/web-shared';
import { readCredentials } from '../lib/auth/redeemCredentials.ts';
import { OneTimeLinkSession, PasswordSetSignInFailed } from '../lib/auth/oneTimeLinkSession.ts';

const MIN_PASSWORD_LENGTH = 8;

type Phase = 'confirm' | 'redeeming' | 'set-password' | 'password-set';

/**
 * The page an admin INVITE lands on — and, for a recovery link that arrives
 * here directly (AdminLogin now forwards those to /reset-password), a recovery
 * link as well. `flow` carries which, and every user-facing string branches on
 * it.
 *
 * NOTHING is redeemed on load — the same gate as ResetPassword, for the same
 * reason. The invite email carries `?token_hash=…&type=invite` on this page
 * (built server-side, lib/adminInvitation.ts), and mail security scanners that
 * EXECUTE a page (Proofpoint / Mimecast URL sandboxes, some Safe Links modes)
 * used to redeem it for us: the invitee then found their invitation "already
 * used", and the sandbox held a live admin session with no password set. So:
 *
 *  1. 'confirm' — the credential is only READ from the URL, on the first render,
 *     and held in state. The redeem waits for a click on Continue. A sandbox
 *     that also clicks can still spend the link (the invitee then asks for a
 *     resend), but gets no persisted session and cannot set the password
 *     without also filling the form.
 *  2. The click redeems into a memory-only client (createEphemeralAdminSupabase)
 *     and strips the credential from the address bar. The persisted admin
 *     client — and setAdminAccessToken — see nothing yet.
 *  3. Only after updateUser({ password }) succeeds is the session handed to the
 *     admin client and setAdminAccessToken called. That is the page's whole
 *     completion step: the public.users row (name, role) was written by the
 *     inviting route BEFORE the email went out, so there is no accept API call
 *     to order around. Leaving before step 3 revokes the in-memory session
 *     (local scope).
 *
 * Latch, late-redeem revoke and abandon: OneTimeLinkSession, shared with
 * ResetPassword.
 */
export function AdminAcceptInvite() {
  const navigate = useNavigate();
  // Read once, on the first render: step 2 strips the URL, and a re-read after
  // that would find nothing. `kind` also tells verifyOtp which flow it is.
  const [{ creds, kind }] = useState(() =>
    readCredentials(window.location.search, window.location.hash),
  );
  const flow = kind;
  const [phase, setPhase] = useState<Phase>('confirm');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Nothing redeemable is refused up front, as on ResetPassword.
  const [error, setError] = useState<unknown>(() =>
    creds ? null : new Error('This link is missing the credentials needed to continue.'),
  );
  const [session] = useState(
    () => new OneTimeLinkSession(creds, kind, createEphemeralAdminSupabase),
  );

  // Leaving (a link away, closing the tab) with a redeemed but unfinished
  // session revokes it, so the live session never outlives the tab.
  useEffect(() => {
    session.enter();
    const abandon = () => session.leave();
    window.addEventListener('pagehide', abandon);
    return () => {
      window.removeEventListener('pagehide', abandon);
      abandon();
    };
  }, [session]);

  async function onContinue() {
    if (session.held) return;
    setError(null);
    setPhase('redeeming');
    try {
      const outcome = await session.redeem();
      if (outcome.status !== 'redeemed') return;
      // Only reached with a real session — see redeemCredentials.
      navigate('/accept-invite', { replace: true });
      setEmail(outcome.email);
      setPhase('set-password');
    } catch (err) {
      setError(err);
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    if (!session.held) {
      setError(
        new Error(
          flow === 'recovery'
            ? 'This reset link is no longer active. Request a new one.'
            : 'This invitation is no longer active. Ask whoever invited you to send a new one.',
        ),
      );
      return;
    }
    setSubmitting(true);
    try {
      const token = await session.complete(getAdminSupabase().auth, password);
      setAdminAccessToken(token);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof PasswordSetSignInFailed) setPhase('password-set');
      else setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  const recovery = flow === 'recovery';
  const title = recovery ? 'Reset your password' : 'Welcome';
  const subtitle = recovery
    ? 'Choose a new password to finish resetting your account.'
    : 'Set a password to finish setting up your admin account.';
  const submitLabel = recovery ? 'Update password and sign in' : 'Set password and sign in';

  /** The tenant's logo, or its name. Shared by every state. */
  const lockup = <SignInBrandLogo />;

  if (phase === 'confirm' || phase === 'redeeming') {
    if (error) {
      return (
        <SignInLayout audience="admin">
          {lockup}
          {/* The heading was `branding.companyName ?? 'Compliance'` — the
              workspace's name as an <h1>, with what actually went wrong demoted
              to a paragraph beneath it. Same fix as ResetPassword: the heading
              states the problem, the lockup above still carries the tenant. */}
          <SignInHeading
            eyebrow="Admin portal"
            title={recovery ? 'This reset link didn’t work' : 'This invitation didn’t work'}
          >
            It may have expired, or it may already have been used — these links work once.
          </SignInHeading>

          <div role="alert" className="empty:hidden">
            <ErrorMessage error={error} />
          </div>

          <SignInRule />

          {/* This branch previously offered NO action at all, on either flow —
              the page simply ended. Recovery is self-serve, so it gets the
              request-another link. An invitation is not: only an existing admin
              can issue one, so the honest instruction is to go and ask, rather
              than a link that would dead-end. */}
          <SignInHelp>
            {recovery ? (
              <div>
                <Link to="/forgot-password">Request a new reset link</Link>
              </div>
            ) : (
              <div>Ask whoever invited you to send a new invitation.</div>
            )}
            <div>
              <Link to="/login">Back to sign in</Link>
            </div>
            <PoweredByFooter />
          </SignInHelp>
        </SignInLayout>
      );
    }
    if (phase === 'confirm') {
      return (
        <SignInLayout audience="admin">
          {lockup}
          <SignInHeading eyebrow="Admin portal" title={title}>
            {recovery
              ? 'Continue to choose a new password for your account.'
              : 'Continue to set a password and finish setting up your admin account.'}
          </SignInHeading>

          <SignInButton audience="admin" type="button" onClick={() => void onContinue()}>
            {recovery ? 'Continue to reset your password' : 'Continue to set up your account'}
          </SignInButton>

          <SignInRule />

          <SignInHelp>
            <div>
              <Link to="/login">Back to sign in</Link>
            </div>
            <PoweredByFooter />
          </SignInHelp>
        </SignInLayout>
      );
    }
    return (
      <SignInLayout audience="admin">
        <div className="flex flex-col items-center gap-4 py-10">
          <CenteredSpinner />
          <p className="m-0" style={{ fontSize: 15, color: '#5B5950' }}>
            Preparing your account…
          </p>
        </div>
      </SignInLayout>
    );
  }

  if (phase === 'password-set') {
    return (
      <SignInLayout audience="admin">
        {lockup}
        <SignInHeading eyebrow="Admin portal" title="Password set">
          Your password was set, but we couldn’t sign you in automatically. Sign in with your new
          password.
        </SignInHeading>

        <SignInRule />

        <SignInHelp>
          <div>
            <Link to="/login">Go to sign in</Link>
          </div>
          <PoweredByFooter />
        </SignInHelp>
      </SignInLayout>
    );
  }

  return (
    <SignInLayout audience="admin">
      {lockup}

      <SignInHeading eyebrow="Admin portal" title={title}>
        {subtitle}
        {email && (
          <>
            {' '}
            You are setting the password for{' '}
            <strong className="font-semibold text-[#14120D]">{email}</strong>.
          </>
        )}
      </SignInHeading>

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <SignInField
          label="New password"
          audience="admin"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
        >
          <SignInPasswordInput
            audience="admin"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
        </SignInField>

        <SignInField label="Confirm password" audience="admin">
          <SignInPasswordInput
            audience="admin"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
        </SignInField>

        <div role="alert" className="empty:hidden">
          <ErrorMessage error={error} />
        </div>

        <SignInButton
          audience="admin"
          type="submit"
          loading={submitting}
          disabled={!password || !confirm}
        >
          {submitLabel}
        </SignInButton>
      </form>

      <SignInRule />

      <SignInHelp>
        <PoweredByFooter />
      </SignInHelp>
    </SignInLayout>
  );
}
